import React from 'react';
import { Button, Chip, SearchField, SelectField, Skeleton } from './controls';
import { Overlay } from './Dialog';
import { Translate } from './i18n';
import { ACTIVE_BG, DIVIDER, MONO, MUTED, TEXT } from './tokens';

export interface PickerUnit {
  id: string;
  name: string;
  /** Already a line on the document. */
  taken: boolean;
}

export interface PickerRow {
  id: string;
  name: string;
  sku?: string | null;
  group?: string | null;
  units: PickerUnit[];
  selected: boolean;
  unitId: string;
}

interface ProductPickerProps {
  open: boolean;
  phone: boolean;
  t: Translate;
  title: string;
  search: string;
  onSearch: (value: string) => void;
  groups: { id: string; name: string }[];
  groupId: string;
  onGroup: (id: string) => void;
  rows: PickerRow[];
  loading: boolean;
  hasMore: boolean;
  onMore: () => void;
  onToggle: (row: PickerRow) => void;
  onUnit: (row: PickerRow, unitId: string) => void;
  selectedCount: number;
  onCancel: () => void;
  onAdd: () => void;
  /** A product already on the document, e.g. "On this transfer"; orders say "On order". */
  takenLabel?: string;
  /** A product with some units on the document; orders say "On order: {units}". */
  takenUnitsLabel?: (units: string) => string;
  /** One of those units in the unit list; orders say "{unit} · on order". */
  takenUnitLabel?: (unit: string) => string;
  /** Hidden label of a row's unit list. */
  unitLabel?: (product: string) => string;
  searchPlaceholder?: string;
}

const isFullyTaken = (row: PickerRow) => row.units.length > 0 && row.units.every((unit) => unit.taken);

export const ProductPicker: React.FC<ProductPickerProps> = ({
  open,
  phone,
  t,
  title,
  search,
  onSearch,
  groups,
  groupId,
  onGroup,
  rows,
  loading,
  hasMore,
  onMore,
  onToggle,
  onUnit,
  selectedCount,
  onCancel,
  onAdd,
  takenLabel,
  takenUnitsLabel,
  takenUnitLabel,
  unitLabel,
  searchPlaceholder,
}) => {
  const addLabel = selectedCount === 1 ? t('Add 1 product') : t('Add {count} products', { count: selectedCount });
  const footer = phone ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
        <p aria-live="polite" style={{ fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
          {t('{count} selected', { count: selectedCount })}
        </p>
        <Button variant="text" phone onClick={onCancel}>
          {t('Cancel')}
        </Button>
      </div>
      <Button variant="primary" phone fullWidth disabled={selectedCount === 0} onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <p aria-live="polite" style={{ flex: '1 1 auto', fontSize: '14px', lineHeight: '20px', color: MUTED, fontVariantNumeric: 'tabular-nums' }}>
        {t('{count} selected', { count: selectedCount })}
      </p>
      <Button onClick={onCancel}>{t('Cancel')}</Button>
      <Button variant="primary" disabled={selectedCount === 0} onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  );

  return (
    <Overlay open={open} phone={phone} title={title} titleId="pu-picker-title" closeLabel={t('Close')} onClose={onCancel} footer={footer}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <SearchField phone={phone} label={t('Search products')} placeholder={searchPlaceholder ?? t('Name, SKU or barcode')} value={search} onChange={onSearch} />
        {groups.length > 0 && (
          <div role="group" aria-label={t('Group')} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            <Chip phone={phone} pressed={!groupId} onClick={() => onGroup('')}>
              {t('All')}
            </Chip>
            {groups.map((group) => (
              <Chip key={group.id} phone={phone} pressed={groupId === group.id} onClick={() => onGroup(group.id)}>
                {group.name}
              </Chip>
            ))}
          </div>
        )}
        <ul aria-label={t('Products')} style={{ margin: phone ? '0 -16px' : '0 -24px', borderTop: `1px solid ${DIVIDER}` }}>
          {rows.map((row) => {
            const full = isFullyTaken(row);
            const takenNames = row.units.filter((unit) => unit.taken).map((unit) => unit.name);
            const meta = [row.group, full ? takenLabel ?? t('On order') : takenNames.length ? (takenUnitsLabel ? takenUnitsLabel(takenNames.join(', ')) : t('On order: {units}', { units: takenNames.join(', ') })) : null].filter(Boolean).join(' · ');
            const unitSelectId = `pu-pick-unit-${row.id}`;
            return (
              <li
                key={row.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: phone ? '10px 16px' : '12px 24px',
                  borderBottom: `1px solid ${DIVIDER}`,
                  background: row.selected && !full ? ACTIVE_BG : undefined,
                }}
              >
                <label style={{ flex: '1 1 auto', minWidth: 0, minHeight: phone ? '44px' : undefined, display: 'flex', alignItems: 'center', gap: '12px', cursor: full ? 'not-allowed' : 'pointer' }}>
                  <input className="pu-check" type="checkbox" checked={full || row.selected} disabled={full} onChange={() => onToggle(row)} style={{ width: '20px', height: '20px', flex: 'none', margin: 0 }} />
                  <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                    <span
                      style={{
                        minWidth: 0,
                        fontSize: phone ? '15px' : '14px',
                        lineHeight: phone ? '22px' : '20px',
                        fontWeight: 500,
                        color: full ? MUTED : TEXT,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {row.name}
                    </span>
                    <span style={{ fontSize: '12.5px', lineHeight: '18px', color: MUTED }}>
                      {row.sku && <span style={{ fontFamily: MONO, fontSize: '12px' }}>{row.sku}</span>}
                      {row.sku && meta ? ' · ' : ''}
                      {meta}
                    </span>
                  </span>
                </label>
                <label className="pu-sr" htmlFor={unitSelectId}>
                  {unitLabel ? unitLabel(row.name) : t('Order unit for {product}', { product: row.name })}
                </label>
                <SelectField
                  id={unitSelectId}
                  phone={phone}
                  disabled={full || row.units.length === 0}
                  value={row.unitId}
                  onChange={(event) => onUnit(row, event.target.value)}
                  wrapperStyle={{ flex: 'none', width: phone ? '112px' : '140px' }}
                >
                  {row.units.length === 0 && <option value="">{t('No unit')}</option>}
                  {row.units.map((unit) => (
                    <option key={unit.id} value={unit.id} disabled={unit.taken}>
                      {/* A product that is on the document already says so once, in its meta line. */}
                      {unit.taken && !full ? (takenUnitLabel ? takenUnitLabel(unit.name) : t('{unit} · on order', { unit: unit.name })) : unit.name}
                    </option>
                  ))}
                </SelectField>
              </li>
            );
          })}
          {loading &&
            [0, 1, 2].map((index) => (
              <li key={`loading-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: phone ? '20px 16px' : '22px 24px', borderBottom: `1px solid ${DIVIDER}` }}>
                <Skeleton width="20px" height={20} radius={4} />
                <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Skeleton width="40%" />
                  <Skeleton width="24%" />
                </div>
                <Skeleton width={phone ? '112px' : '140px'} height={phone ? 44 : 40} radius={10} />
              </li>
            ))}
        </ul>
        {!loading && rows.length === 0 && (
          <p style={{ padding: '16px 0', fontSize: '14px', lineHeight: '20px', color: MUTED, textAlign: 'center' }}>
            {search ? t('No products match "{search}". Check the spelling or search by SKU.', { search }) : t('No products in this group')}
          </p>
        )}
        {hasMore && !loading && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button variant="text" phone={phone} onClick={onMore}>
              {t('Show more products')}
            </Button>
          </div>
        )}
      </div>
    </Overlay>
  );
};
